import { useCallback, useEffect, useRef, useState } from "react";
import { Wallet, TrendingUp, Receipt, Users, Radio, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { rp, rangeFor, tzOffset, fmtDate, fmtTime, METHODS } from "../lib/format";
import { StatCard, PageWrap, Empty } from "../components/common";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const POLL_MS = 3000;

export default function Live() {
  const [d, setD] = useState(null);
  const [updated, setUpdated] = useState(null);
  const [fresh, setFresh] = useState(new Set());
  const known = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/live", { params: { ...rangeFor("today"), tz_offset: tzOffset() } }).catch(() => ({ data: null }));
    if (!data) return;
    const ids = new Set(data.recent.map((t) => t.id));
    if (known.current) setFresh(new Set([...ids].filter((x) => !known.current.has(x))));
    known.current = ids;
    setD(data);
    setUpdated(new Date());
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => document.visibilityState === "visible" && load(), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const s = d?.summary;
  return (
    <PageWrap>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="relative flex h-3 w-3"><span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-75 animate-ping" /><span className="relative inline-flex h-3 w-3 rounded-full bg-success" /></span>
          <span className="font-semibold">Live · Hari Ini</span>
          <span className="text-xs text-muted-foreground" data-testid="live-updated">{updated ? `diperbarui ${fmtTime(updated)}` : "memuat..."}</span>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="live-refresh-btn"><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
        <StatCard testid="live-revenue" icon={Wallet} label="Penjualan" value={rp(s?.revenue)} />
        <StatCard testid="live-profit" icon={TrendingUp} tone="success" label="Laba Kotor" value={rp(s?.profit)} />
        <StatCard testid="live-count" icon={Receipt} tone="chart-5" label="Transaksi" value={s?.count ?? 0} hint={`${s?.items_sold || 0} item`} />
        <StatCard testid="live-shifts" icon={Users} tone="warning" label="Kasir Aktif" value={d?.open_shifts?.length ?? 0} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 rounded-2xl border bg-card p-5">
          <h2 className="font-display font-bold mb-3 flex items-center gap-2"><Radio className="h-4 w-4 text-primary" />Transaksi Masuk</h2>
          <div className="divide-y" data-testid="live-recent-list">
            {(d?.recent || []).map((t) => (
              <div key={t.id} className={`flex items-center justify-between py-3 gap-3 transition-colors ${fresh.has(t.id) ? "bg-primary/10 -mx-2 px-2 rounded-lg fade-up" : ""}`}>
                <div className="min-w-0">
                  <div className="font-mono text-sm font-semibold truncate">{t.invoice}{t.offline && <span className="ml-2 text-[10px] font-sans rounded-full border px-1.5 py-0.5 text-warning">offline</span>}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(t.created_at)} · {t.cashier_name}</div>
                </div>
                <div className="text-right">
                  <div className={`font-mono font-bold ${t.status === "batal" ? "line-through text-muted-foreground" : ""}`}>{rp(t.total)}</div>
                  <div className="text-xs text-muted-foreground">{METHODS[t.payment_method]}</div>
                </div>
              </div>
            ))}
            {d && !d.recent.length && <Empty icon={Receipt} text="Belum ada transaksi." />}
          </div>
        </div>
        <div className="space-y-6">
          <div className="rounded-2xl border bg-card p-5">
            <h2 className="font-display font-bold mb-3">Shift Berjalan</h2>
            <div className="space-y-2" data-testid="live-open-shifts">
              {(d?.open_shifts || []).map((sh) => (
                <div key={sh.id} className="rounded-xl bg-muted/50 p-3">
                  <div className="flex justify-between items-center"><span className="font-semibold text-sm">{sh.cashier_name}</span><Badge variant="outline" className="text-success">Aktif</Badge></div>
                  <div className="text-xs text-muted-foreground mt-1">Sejak {fmtDate(sh.opened_at)}</div>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                    <div>Penjualan<div className="font-mono font-bold text-sm">{rp(sh.total_sales)}</div></div>
                    <div>Kas di laci<div className="font-mono font-bold text-sm">{rp(sh.expected_cash)}</div></div>
                  </div>
                </div>
              ))}
              {d && !d.open_shifts.length && <p className="text-sm text-muted-foreground">Tidak ada shift aktif.</p>}
            </div>
          </div>
          <div className="rounded-2xl border bg-card p-5">
            <h2 className="font-display font-bold mb-3">Per Kasir Hari Ini</h2>
            {(s?.by_cashier || []).map((c) => (
              <div key={c.cashier_id} className="flex justify-between py-1.5 text-sm"><span>{c.name} <span className="text-muted-foreground">({c.count})</span></span><span className="font-mono font-semibold">{rp(c.total)}</span></div>
            ))}
            {s && !s.by_cashier.length && <p className="text-sm text-muted-foreground">Belum ada penjualan.</p>}
          </div>
        </div>
      </div>
    </PageWrap>
  );
}
